/**
 * BEI (Board for Evaluation of Interpreters) Configuration
 * Texas-specific certification requirements and logic
 */

// BEI Certificate Types and their CEU requirements
export const BEI_CERTIFICATE_TYPES = {
  'BEI General': {
    name: 'BEI General Certificate',
    totalRequired: 10.0,
    cycleYears: 5,
    requirements: {
      'Interpreting': { min: 6.0, max: 10.0 },
      'Ethics': { min: 2.0, max: 10.0 },
      'General': { min: 0.0, max: 2.0 }
    },
    description: 'Standard BEI General Certificate - 6 Interpreting + 2 Ethics + up to 2 General'
  },
  
  'BEI General + Court': {
    name: 'BEI General + Court Certificate',
    totalRequired: 10.0,
    cycleYears: 5,
    requirements: {
      'Interpreting': { min: 6.0, max: 6.0 },
      'Court': { min: 2.0, max: 2.0 },
      'Ethics': { min: 2.0, max: 2.0 }
    },
    description: '6 Interpreting + 2 Court + 2 Ethics'
  },
  
  'BEI General + Medical': {
    name: 'BEI General + Medical Certificate',
    totalRequired: 10.0,
    cycleYears: 5,
    requirements: {
      'Interpreting': { min: 6.0, max: 6.0 },
      'Medical': { min: 2.0, max: 2.0 },
      'Ethics': { min: 2.0, max: 2.0 }
    },
    description: '6 Interpreting + 2 Medical + 2 Ethics'
  },
  
  'BEI General + Trilingual': {
    name: 'BEI General + Trilingual Certificate',
    totalRequired: 10.0,
    cycleYears: 5,
    requirements: {
      'Interpreting': { min: 5.0, max: 5.0 },
      'Trilingual': { min: 3.0, max: 3.0 },
      'Ethics': { min: 2.0, max: 2.0 }
    },
    description: '5 Interpreting + 3 Trilingual + 2 Ethics'
  },
  
  'BEI Court + RID': {
    name: 'BEI Court Certificate + Current RID',
    totalRequired: 2.0,
    cycleYears: 5,
    requirements: {
      'Court': { min: 2.0, max: 2.0 }
    },
    description: 'Only 2 Court CEUs required (RID handles other requirements)'
  },
  
  'BEI Medical + RID': {
    name: 'BEI Medical Certificate + Current RID',
    totalRequired: 2.0,
    cycleYears: 5,
    requirements: {
      'Medical': { min: 2.0, max: 2.0 }
    },
    description: 'Only 2 Medical CEUs required (RID handles other requirements)'
  },
  
  'BEI Trilingual + RID': {
    name: 'BEI Trilingual Certificate + Current RID',
    totalRequired: 3.0,
    cycleYears: 5,
    requirements: {
      'Trilingual': { min: 3.0, max: 3.0 }
    },
    description: 'Only 3 Trilingual CEUs required (RID handles other requirements)'
  }
};

// BEI-specific CEU categories and subcategories
export const BEI_CATEGORIES = {
  'Interpreting': {
    label: 'Interpreting-Related Topics',
    description: 'Topics directly related to interpreting skills and techniques',
    subcategories: [
      { value: 'ASL Linguistics', label: 'ASL Linguistics', description: 'American Sign Language structure and linguistics' },
      { value: 'Interpreting Techniques', label: 'Interpreting Techniques', description: 'Interpreting methods and strategies' },
      { value: 'Transliteration', label: 'Transliteration', description: 'English-based signing and transliteration' },
      { value: 'Consecutive Interpreting', label: 'Consecutive Interpreting', description: 'Consecutive interpretation techniques' },
      { value: 'Simultaneous Interpreting', label: 'Simultaneous Interpreting', description: 'Simultaneous interpretation skills' },
      { value: 'Sight Translation', label: 'Sight Translation', description: 'Document translation and sight reading' },
      { value: 'Voice Interpreting', label: 'Voice Interpreting', description: 'Deaf-to-hearing voice interpretation' },
      { value: 'Team Interpreting', label: 'Team Interpreting', description: 'Working with interpreter teams' },
      { value: 'Platform Interpreting', label: 'Platform Interpreting', description: 'Large group and platform interpreting' },
      { value: 'Video Relay', label: 'Video Relay Services (VRS)', description: 'Video relay interpreting' },
      { value: 'Video Remote', label: 'Video Remote Interpreting (VRI)', description: 'Remote video interpreting' }
    ]
  },
  
  'Ethics': {
    label: 'Ethics-Related Topics',
    description: 'Professional ethics, standards, and conduct',
    subcategories: [
      { value: 'Professional Ethics', label: 'Professional Ethics', description: 'Interpreter ethics and professional standards' },
      { value: 'Code of Conduct', label: 'Code of Professional Conduct', description: 'BEI and professional codes of conduct' },
      { value: 'Confidentiality', label: 'Confidentiality', description: 'Maintaining confidentiality and privacy' },
      { value: 'Boundaries', label: 'Professional Boundaries', description: 'Appropriate professional relationships' },
      { value: 'Impartiality', label: 'Impartiality', description: 'Maintaining neutrality and impartiality' },
      { value: 'Cultural Mediation', label: 'Cultural Mediation', description: 'Navigating cultural differences ethically' },
      { value: 'Ethical Decision Making', label: 'Ethical Decision Making', description: 'Making ethical choices in practice' },
      { value: 'Professional Development', label: 'Professional Development', description: 'Continuing professional growth' },
      { value: 'Business Ethics', label: 'Business Ethics', description: 'Ethical business practices for interpreters' }
    ]
  },
  
  'Court': {
    label: 'Court-Related Topics',
    description: 'Legal interpreting and court procedures (DHHS approved)',
    subcategories: [
      { value: 'Legal Terminology', label: 'Legal Terminology', description: 'Legal vocabulary and concepts' },
      { value: 'Court Procedures', label: 'Court Procedures', description: 'Understanding court processes and protocols' },
      { value: 'Criminal Law', label: 'Criminal Law', description: 'Criminal proceedings and terminology' },
      { value: 'Civil Law', label: 'Civil Law', description: 'Civil proceedings and terminology' },
      { value: 'Family Law', label: 'Family Law', description: 'Family court and custody proceedings' },
      { value: 'Juvenile Law', label: 'Juvenile Law', description: 'Juvenile court proceedings' },
      { value: 'Legal Ethics', label: 'Legal Ethics', description: 'Ethics specific to legal interpreting' },
      { value: 'Court Interpreting Skills', label: 'Court Interpreting Skills', description: 'Specialized court interpreting techniques' },
      { value: 'Legal Document Translation', label: 'Legal Document Translation', description: 'Translating legal documents' },
      { value: 'Deposition Interpreting', label: 'Deposition Interpreting', description: 'Interpreting for depositions' }
    ]
  },
  
  'Medical': {
    label: 'Medical-Related Topics',
    description: 'Healthcare interpreting and medical terminology (DHHS approved)',
    subcategories: [
      { value: 'Medical Terminology', label: 'Medical Terminology', description: 'Healthcare vocabulary and concepts' },
      { value: 'Healthcare Systems', label: 'Healthcare Systems', description: 'Understanding healthcare delivery systems' },
      { value: 'Patient Care', label: 'Patient Care', description: 'Patient interaction and care processes' },
      { value: 'Mental Health', label: 'Mental Health', description: 'Mental health and psychiatric interpreting' },
      { value: 'Emergency Medicine', label: 'Emergency Medicine', description: 'Emergency room and trauma interpreting' },
      { value: 'Surgical Procedures', label: 'Surgical Procedures', description: 'Operating room and surgical interpreting' },
      { value: 'Rehabilitation', label: 'Rehabilitation', description: 'Physical and occupational therapy' },
      { value: 'Pediatric Medicine', label: 'Pediatric Medicine', description: 'Children\'s healthcare interpreting' },
      { value: 'Medical Ethics', label: 'Medical Ethics', description: 'Healthcare ethics and HIPAA compliance' },
      { value: 'Telemedicine', label: 'Telemedicine', description: 'Remote healthcare interpreting' }
    ]
  },
  
  'Trilingual': {
    label: 'Trilingual-Related Topics',
    description: 'ASL-English-Spanish interpreting',
    subcategories: [
      { value: 'Spanish Language', label: 'Spanish Language Skills', description: 'Spanish language proficiency and grammar' },
      { value: 'Cultural Competency', label: 'Latino/Hispanic Cultural Competency', description: 'Understanding Latino/Hispanic cultures' },
      { value: 'Trilingual Techniques', label: 'Trilingual Interpreting Techniques', description: 'Three-language interpreting strategies' },
      { value: 'Code Switching', label: 'Code Switching', description: 'Managing language switching and mixing' },
      { value: 'Regional Variations', label: 'Regional Spanish Variations', description: 'Different Spanish dialects and regions' },
      { value: 'Educational Settings', label: 'Educational Trilingual', description: 'School-based trilingual interpreting' },
      { value: 'Medical Trilingual', label: 'Medical Trilingual', description: 'Healthcare trilingual interpreting' },
      { value: 'Legal Trilingual', label: 'Legal Trilingual', description: 'Court trilingual interpreting' },
      { value: 'Community Trilingual', label: 'Community Trilingual', description: 'Community-based trilingual services' }
    ]
  },
  
  'General': {
    label: 'General Studies',
    description: 'Non-interpreting related training (maximum 2.0 CEUs)',
    subcategories: [
      { value: 'College Courses', label: 'College Credit Courses', description: 'Non-interpreting college coursework (e.g., MBA, psychology, business administration)' },
      { value: 'Professional Development', label: 'General Professional Development', description: 'Career development not specific to interpreting' },
      { value: 'Technology Training', label: 'General Technology Skills', description: 'Technology skills not specific to interpreting tools' },
      { value: 'Business Skills', label: 'General Business Skills', description: 'Business and entrepreneurship not related to interpreting business' },
      { value: 'Communication Skills', label: 'General Communication Training', description: 'Communication skills not specific to interpreting' },
      { value: 'Leadership', label: 'Leadership Training', description: 'General leadership and management skills' },
      { value: 'Health Wellness', label: 'Health & Wellness', description: 'General health, fitness, and wellness training' },
      { value: 'Creative Arts', label: 'Creative Arts', description: 'Art, music, writing, and other creative pursuits' },
      { value: 'Academic Subjects', label: 'Academic Subjects', description: 'General academic subjects (math, science, history, etc.)' }
    ]
  }
};

// Special BEI rules and validations
export const BEI_RULES = {
  // E/I codes can be applied to either Ethics or Interpreting, but not both
  EI_CODE_RULE: 'E/I coded CEUs may be applied to either Ethics or Interpreting category, but not both to avoid duplication.',
  
  // General CEUs are not required but limited
  GENERAL_OPTIONAL: 'General CEUs are not required. You can meet the 10 CEU requirement through Interpreting and Ethics CEUs alone.',
  
  // First-time CEU credit rules
  FIRST_TIME_CREDIT: 'First-time CEUs credit is approved only once in a five-year certification period.',
  
  // CEU carryover rules
  NO_CARRYOVER: 'CEUs may not be carried over from one five-year cycle to another.',
  
  // Multiple certificate rules
  MULTIPLE_CERT_RESET: 'When awarded an additional BEI certificate, all certificates get a new 5-year cycle starting from the newest certificate award date. Any CEUs earned before this date become void.',
  
  // Annual renewal requirement
  ANNUAL_RENEWAL: 'BEI certificates must be renewed annually in addition to the 5-year CEU cycle.'
};

/**
 * Get BEI requirements for a specific certificate type
 * @param {string} certificateType - BEI certificate type
 * @returns {Object} Requirements object
 */
export function getBEIRequirements(certificateType) {
  return BEI_CERTIFICATE_TYPES[certificateType] || BEI_CERTIFICATE_TYPES['BEI General'];
}

/**
 * Calculate BEI CEU progress for a user
 * @param {Array} ceus - Array of CEU records
 * @param {string} certificateType - BEI certificate type
 * @returns {Object} Progress calculation
 */
export function calculateBEIProgress(ceus, certificateType) {
  const requirements = getBEIRequirements(certificateType);
  const progress = {
    total: 0,
    byCategory: {},
    requirements: requirements.requirements,
    totalRequired: requirements.totalRequired,
    cycleYears: requirements.cycleYears,
    isComplete: false,
    warnings: [],
    certificateType
  };

  // Initialize category totals
  Object.keys(requirements.requirements).forEach(category => {
    progress.byCategory[category] = 0;
  });

  // Calculate totals by category
  ceus.forEach(ceu => {
    const hours = parseFloat(ceu.ceuHours) || 0;
    progress.total += hours;
    
    if (progress.byCategory.hasOwnProperty(ceu.category)) {
      progress.byCategory[ceu.category] += hours;
    }
  });

  // Check if requirements are met
  let requirementsMet = true;
  Object.entries(requirements.requirements).forEach(([category, req]) => {
    const earned = progress.byCategory[category] || 0;
    
    if (earned < req.min) {
      requirementsMet = false;
      progress.warnings.push(`Need ${(req.min - earned).toFixed(1)} more ${category} CEUs (minimum ${req.min})`);
    }
    
    if (earned > req.max) {
      progress.warnings.push(`Exceeded maximum for ${category}: ${earned.toFixed(1)}/${req.max} CEUs`);
    }
  });

  progress.isComplete = requirementsMet && progress.total >= requirements.totalRequired;

  // Add general warnings
  if (progress.total < requirements.totalRequired) {
    progress.warnings.push(`Need ${(requirements.totalRequired - progress.total).toFixed(1)} more CEUs total`);
  }

  return progress;
}

/**
 * Validate a CEU against BEI rules
 * @param {Object} ceu - CEU record
 * @param {Array} existingCeus - Existing CEU records
 * @param {string} certificateType - BEI certificate type
 * @returns {Object} Validation result
 */
export function validateBEICEU(ceu, existingCeus, certificateType) {
  const validation = {
    isValid: true,
    errors: [],
    warnings: []
  };

  const requirements = getBEIRequirements(certificateType);
  
  // Check if category is valid for this certificate type
  if (!requirements.requirements.hasOwnProperty(ceu.category)) {
    validation.isValid = false;
    validation.errors.push(`${ceu.category} is not a valid category for ${certificateType}`);
  }

  // Check for duplicate workshops (same workshop can only be counted once per cycle)
  const duplicateWorkshop = existingCeus.find(existing => 
    existing.id !== ceu.id && 
    existing.workshopName.toLowerCase() === ceu.workshopName.toLowerCase() &&
    existing.instructor.toLowerCase() === ceu.instructor.toLowerCase()
  );
  
  if (duplicateWorkshop) {
    validation.warnings.push('This workshop may have already been completed in this cycle. BEI only allows credit for a workshop once per 5-year cycle.');
  }

  // Check General category limit
  if (ceu.category === 'General') {
    const currentGeneral = existingCeus
      .filter(existing => existing.id !== ceu.id && existing.category === 'General')
      .reduce((sum, existing) => sum + parseFloat(existing.ceuHours), 0);
    
    const newTotal = currentGeneral + parseFloat(ceu.ceuHours);
    const maxGeneral = requirements.requirements.General?.max || 2.0;
    
    if (newTotal > maxGeneral) {
      validation.warnings.push(`This would exceed the maximum General CEUs allowed (${maxGeneral}). Current: ${currentGeneral}, Adding: ${ceu.ceuHours}`);
    }
  }

  return validation;
}

const beiConfig = {
  BEI_CERTIFICATE_TYPES,
  BEI_CATEGORIES,
  BEI_RULES,
  getBEIRequirements,
  calculateBEIProgress,
  validateBEICEU
};

export default beiConfig;
